<script setup lang="ts">
import type { CentlexFlexion } from "@/types/article-formats/centlex";

const props = defineProps<{
  lemma: string;
  flexions: CentlexFlexion[];
}>();

type FlexionNumber = 1 | 2 | 3 | 4;
const hasFlexion = (flexion: CentlexFlexion, number: FlexionNumber): boolean => {
  switch (number) {
    case 1:
      return Boolean(flexion.alias1 || flexion.flex1);
    case 2:
      return Boolean(flexion.alias2 || flexion.flex2);
    case 3:
      return Boolean(flexion.alias3 || flexion.flex3);
    case 4:
      return Boolean(flexion.alias4 || flexion.flex4);
  }
};
const hasAnyFlexion = (flexion: CentlexFlexion, numbers: FlexionNumber[]): boolean => {
  for (const number of numbers) {
    if (hasFlexion(flexion, number)) {
      return true;
    }
  }

  return false;
};

const hasWordClass = (wordClass: number[]): boolean => {
  if (!props.flexions[0].wordClass) {
    return false;
  }

  return wordClass.includes(props.flexions[0].wordClass);
};

const displayDelimiter = (delimiter: number | null, element: string): string =>
  delimiter ? `${element} ` : "";

const displayFlexion = (flexion: CentlexFlexion, number: FlexionNumber): string => {
  switch (number) {
    case 1:
      return (flexion.alias1 ? props.lemma : "") + (flexion.flex1 ?? "");
    case 2:
      return (flexion.alias2 ? props.lemma : "") + (flexion.flex2 ?? "");
    case 3:
      return (flexion.alias3 ? props.lemma : "") + (flexion.flex3 ?? "");
    case 4:
      return (flexion.alias4 ? props.lemma : "") + (flexion.flex4 ?? "");
  }
};
</script>

<template>
  <span title="Bøjninger" class="article-header__inflections">
    <span v-for="(flexion, flexionIndex) in props.flexions" :key="flexionIndex">
      <template v-if="hasAnyFlexion(flexion, [1, 2, 3, 4])">
        <template v-if="flexion.priority && flexion.priority > 1">
          <br /><span class="article-header__inflections-comment">også, men anbefales ikke</span>
        </template>

        <template v-if="hasWordClass([1, 2, 14])">
          &lt;<span title="Kendeord">{{ displayFlexion(flexion, 1) }}</span
          >{{ displayDelimiter(flexion.delimiter1, ";")
          }}<span title="Bestemt ental">{{ displayFlexion(flexion, 2) }}</span
          >{{ displayDelimiter(flexion.delimiter2, ",")
          }}<span title="Ubestemt flertal">{{ displayFlexion(flexion, 3) }}</span
          >{{ displayDelimiter(flexion.delimiter3, ",")
          }}<span title="Bestemt flertal">{{ displayFlexion(flexion, 4) }}</span
          >&gt;
        </template>
        <template v-else-if="hasWordClass([3])">
          &lt;<span title="Nutid">{{ displayFlexion(flexion, 1) }}</span
          >{{ displayDelimiter(flexion.delimiter1, ",")
          }}<span title="Datid">{{ displayFlexion(flexion, 2) }}</span
          >{{ displayDelimiter(flexion.delimiter2, ",")
          }}<span title="Før nutid">{{ displayFlexion(flexion, 3) }}</span
          >&gt;
        </template>
        <template v-else-if="hasWordClass([4, 5])">
          <template v-if="hasAnyFlexion(flexion, [1, 2])">
            &lt;<span title="Intetkøn">{{ displayFlexion(flexion, 1) }}</span
            >{{ displayDelimiter(flexion.delimiter1, ",")
            }}<span title="Flertal">{{ displayFlexion(flexion, 2) }}</span
            >&gt;
          </template>

          <template v-if="hasAnyFlexion(flexion, [3, 4])">
            &lt;<span title="2. grad">{{ displayFlexion(flexion, 3) }}</span
            >{{ displayDelimiter(flexion.delimiter3, ",")
            }}<span title="3. grad">{{ displayFlexion(flexion, 4) }}</span
            >&gt;
          </template>
        </template>
      </template>
    </span>
  </span>
</template>
